<template>
  <article
    class="relative 
           p-3 sm:bg-white/80 sm:backdrop-blur-sm sm:rounded-2xl sm:p-6 sm:post-shadow 
           animate-fade-in sm:hover:shadow-lg transition-all duration-300
           border-b border-gray-100 sm:border-none mb-4 sm:mb-4"
    :id="`post-${post.id}`"
  >
    <!-- 編輯與刪除按鈕 -->
    <div v-if="canEdit || canPin" class="absolute top-4 right-4 flex space-x-2">
      <!-- 編輯/刪除操作（作者或管理員） -->
      <template v-if="canEdit">
        <button @click="$emit('edit', post)" class="text-wood-600 hover:text-wood-800 transition-colors" title="編輯">
          <i class="fas fa-edit"></i>
        </button>
        <button @click="handleDelete" class="text-wood-600 hover:text-red-500 transition-colors" title="刪除">
          <i class="fas fa-trash-alt"></i>
        </button>
      </template>
      
      <!-- 管理員置頂操作 -->
      <template v-if="canPin">
        <button
          v-if="!post.pinned"
          @click="handlePin"
          class="text-blue-600 hover:text-blue-800 transition-colors"
          title="置頂貼文"
        >
          <i class="fas fa-thumbtack"></i>
        </button>
        <button
          v-else
          @click="handleUnpin"
          class="text-yellow-600 hover:text-yellow-800 transition-colors"
          title="取消置頂"
        >
          <i class="fas fa-thumbtack transform rotate-45"></i>
        </button>
      </template>
    </div>

    <div class="flex space-x-2 sm:space-x-4">
      <!-- 使用者頭像 -->
      <div class="flex-shrink-0">
        <div @click="navigateToUser" class="cursor-pointer">
          <img :src="post.avatar" class="w-8 h-8 sm:w-12 sm:h-12 rounded-full avatar-glow object-cover" />
        </div>
      </div>

      <!-- 詳情頁面 H1 標籤 - 僅供 SEO 使用，視覺上隱藏 -->
    <h1 v-if="isDetailPage" class="seo-only-h1">
      <span v-if="extractTitle">{{ extractTitle }}</span>
      <span v-else>無標題</span>
    </h1>

    <!-- 貼文內容 -->
    <div class="flex-1 min-w-0">
      <div class="flex flex-wrap items-center gap-1 sm:gap-2 mb-2">
        <span @click="navigateToUser" class="font-semibold text-wood-800 hover:text-wood-900 transition-colors text-lg sm:text-base cursor-pointer">
          {{ post.nickname }}
        </span>
        <span class="cursor-pointer text-base sm:text-sm text-wood-600 hover:text-wood-800 transition-colors" @click="navigateToUser">
          @{{ post.uid }}
        </span>
        <span class="cursor-pointer text-base sm:text-sm text-wood-500"  @click="handleContentClick" >• {{ post.created_at_relative }}</span>
        <span v-if="post.club" class="bg-wood-200 text-wood-700 px-2 py-1 rounded-full text-xs">
          {{ post.club }}
        </span>
          
          <!-- 置頂標籤 -->
          <span v-if="post.is_pinned_display" class="inline-flex items-center bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
            <i class="fas fa-thumbtack mr-1 text-xs"></i>
            置頂
          </span>
          
          <!-- 私密標籤 -->
          <span v-if="post.is_private" class="inline-flex items-center bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
            <i class="fas fa-lock mr-1 text-xs"></i>
            私密
          </span>
        </div>

        <div class="text-wood-700 mb-4 leading-relaxed break-words text-lg sm:text-base">
          <div @click="handleShowMore" class="markdown-content">
            <span v-if="!post.isExpanded && !isDetailPage" v-html="processedShortContent"></span>
            <span v-else v-html="processedLongContent"></span>
          </div>
          <button
            v-if="post.short !== post.long && !isDetailPage"
            @click="handleShowMore"
            class="text-wood-600 hover:text-wood-800 transition-colors font-medium mt-2"
          >
            {{ post.isExpanded ? '...收合' : '...查看更多' }}
          </button>
        </div>

        <!-- 多媒體展示組件 -->
        <MediaDisplay :post="post" />

        <!-- 互動按鈕（已登入使用者）-->
        <template v-if="isLoggedIn">
          <div class="flex items-center justify-between pt-3 sm:pt-4 border-t border-wood-200">
            <button @click="handleCommentClick" class="flex items-center space-x-1 sm:space-x-2 text-wood-600 hover:text-blue-500 transition-colors">
              <i class="far fa-comment text-lg sm:text-base"></i>
              <span class="text-base sm:text-sm">{{ post.comments }}</span>
              <span class="text-base sm:text-sm hidden sm:inline">留言</span>
            </button>
            <button
              @click="$emit('like', post.id)"
              class="flex items-center space-x-1 sm:space-x-2 text-wood-600 hover:text-red-500 transition-colors group"
            >
              <i :class="['fa-heart text-lg sm:text-base', post.isLiked ? 'fas text-red-500' : 'far group-hover:fas']"></i>
              <span class="text-base sm:text-sm">{{ post.likes }}</span>
              <span class="text-base sm:text-sm hidden sm:inline">點讚</span>
            </button>
            <button
              @click="$emit('bookmark', post.id)"
              class="flex items-center space-x-1 sm:space-x-2 text-wood-600 hover:text-yellow-500 transition-colors"
            >
              <i :class="['fa-bookmark text-lg sm:text-base', post.isBookmarked ? 'fas text-yellow-500' : 'far']"></i>
              <span class="text-base sm:text-sm hidden sm:inline">收藏</span>
            </button>
            <button
              @click="handleShareClick"
              class="flex items-center space-x-1 sm:space-x-2 text-wood-600 hover:text-green-500 transition-colors"
            >

              <i class="far fa-solid fa-share-nodes text-lg sm:text-base"></i>
              <span class="text-base sm:text-sm hidden sm:inline">分享</span>
            </button>
          </div>
        </template>

        <!-- 互動按鈕（未登入使用者）-->
        <template v-else>
          <div class="flex items-center justify-between pt-3 sm:pt-4 border-t border-wood-200">
            <button @click="handleCommentClick" class="flex items-center space-x-1 sm:space-x-2 text-wood-600 hover:text-blue-500 transition-colors">
              <i class="far fa-comment text-lg sm:text-base"></i>
              <span class="text-base sm:text-sm">{{ post.comments }}</span>
              <span class="text-base sm:text-sm hidden sm:inline">留言</span>
            </button>
            <div class="flex items-center space-x-1 sm:space-x-2 text-wood-600">
              <i class="far fa-heart text-lg sm:text-base"></i>
              <span class="text-base sm:text-sm">{{ post.likes }}</span>
              <span class="text-base sm:text-sm hidden sm:inline">點讚</span>
            </div>
            <div class="flex items-center space-x-1 sm:space-x-2 text-wood-600">
              <i class="far fa-bookmark text-lg sm:text-base"></i>
              <span class="text-base sm:text-sm hidden sm:inline">收藏</span>
            </div>
            <button
              @click="handleShareClick"
              class="flex items-center space-x-1 sm:space-x-2 text-wood-600 hover:text-green-500 transition-colors"
            >
              <i class="far fa-solid fa-share-nodes text-lg sm:text-base"></i>
              <span class="text-base sm:text-sm hidden sm:inline">分享</span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </article>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue'
import { useUserStore } from '../stores/user.js'
import MediaDisplay from './MediaDisplay.vue'
import { processContent } from '../utils/markdown.js'

const userStore = useUserStore()


const props = defineProps({
  post: {
    type: Object,
    required: true
  },
  isLoggedIn: {
    type: Boolean,
    default: false
  },
  showDetailOnClick: {
    type: Boolean,
    default: false
  },
  isDetailPage: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['like', 'bookmark', 'showMore', 'showDetail', 'edit', 'delete', 'navigateToUser', 'comment', 'pin', 'unpin'])

const isAuthor = computed(() => {
  return props.isLoggedIn && userStore.currentUser && userStore.currentUser.id === props.post.user_id
})

// 檢查是否可以編輯貼文（作者或管理員）
const canEdit = computed(() => {
  if (!props.isLoggedIn || !userStore.currentUser) return false

  // 作者可以編輯自己的貼文
  if (isAuthor.value) return true

  // 系統管理員可以編輯任何貼文
  if (userStore.isAdmin) return true

  return false
})

// 檢查是否可以置頂貼文
const canPin = computed(() => {
  if (!props.isLoggedIn || !userStore.currentUser) return false

  // 系統管理員可以置頂任何貼文
  if (userStore.isAdmin) return true

  // TODO: 武場管理員可以置頂武場內的貼文
  // 這裡需要檢查使用者是否為該武場的管理員
  // 暫時先只允許系統管理員操作
  return false
})

// 處理 markdown 內容
const processedShortContent = computed(() => {
  return processContent(props.post.short || '')
})

const processedLongContent = computed(() => {
  return processContent(props.post.long || '')
})

// 提取貼文標題（用於 H1 標籤）- 優化版本
const extractTitle = computed(() => {
  if (!props.post.long) return ''

  // 簡化處理：直接提取第一行，避免複雜的正則表達式
  const content = props.post.long
  const lineBreakIndex = content.indexOf('\n')

  let firstLine
  if (lineBreakIndex > 0) {
    firstLine = content.substring(0, lineBreakIndex)
  } else {
    firstLine = content
  }

  // 基本清理和長度限制
  return firstLine.trim().substring(0, 80) || ''
})

const handleDelete = () => {
  emit('delete', props.post.id)
}

// 處理置頂操作
const handlePin = () => {
  emit('pin', props.post.id)
}

// 處理取消置頂操作
const handleUnpin = () => {
  emit('unpin', props.post.id)
}


const handleContentClick = () => {
  console.log('Post 內容被點擊，貼文 ID:', props.post.id)
  console.log('showDetailOnClick:', props.showDetailOnClick)
  if (props.showDetailOnClick) {
    console.log('發射 showDetail 事件')
    emit('showDetail', props.post.id)
  }
}

const handleShowMore = () => {
  emit('showMore', props.post.id)
}


// 導航到使用者頁面
const navigateToUser = () => {
  console.log('導航到使用者頁面:', props.post.uid)
  emit('navigateToUser', props.post.uid)
}

// 處理留言點擊，導航到貼文詳情頁
const handleCommentClick = () => {
  console.log('留言按鈕被點擊，導向貼文詳情頁:', props.post.id)
  window.location.href = `/posts/${props.post.id}`
}

// 處理分享按鈕點擊，複製貼文網址到剪貼簿
const handleShareClick = async () => {
  const postUrl = `${window.location.origin}/posts/${props.post.id}`

  try {
    await navigator.clipboard.writeText(postUrl)
    // 簡單的視覺回饋，可以考慮使用通知系統
    console.log('貼文網址已複製到剪貼簿:', postUrl)

    // 暫時使用 alert 提供使用者回饋，之後可以替換為更好的通知系統
    alert('貼文網址已複製到剪貼簿！')
  } catch (err) {
    console.error('複製到剪貼簿失敗:', err)
    // 降級處理：使用舊版 API 或手動選擇文字
    try {
      const textArea = document.createElement('textarea')
      textArea.value = postUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      alert('貼文網址已複製到剪貼簿！')
    } catch (fallbackErr) {
      console.error('降級複製也失敗:', fallbackErr)
      alert(`複製失敗，請手動複製：${postUrl}`)
    }
  }
}

</script>

<style scoped>
/* 貼文陰影 */
.post-shadow {
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(196, 166, 116, 0.1);
}

/* 頭像光暈 */
.avatar-glow {
  box-shadow: 0 0 0 3px rgba(166, 139, 91, 0.3);
  object-fit: cover;
}

/* 動畫效果 */
@keyframes fadeIn {
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

/* 木質色彩系統 */
.text-wood-50 { color: #faf8f3; }
.text-wood-100 { color: #f4f0e6; }
.text-wood-200 { color: #e8dcc0; }
.text-wood-300 { color: #dcc89a; }
.text-wood-400 { color: #c4a674; }
.text-wood-500 { color: #a68b5b; }
.text-wood-600 { color: #8b7355; }
.text-wood-700 { color: #6d5a47; }
.text-wood-800 { color: #4a3f35; }
.text-wood-900 { color: #2d261f; }

.bg-wood-50 { background-color: #faf8f3; }
.bg-wood-100 { background-color: #f4f0e6; }
.bg-wood-200 { background-color: #e8dcc0; }
.bg-wood-300 { background-color: #dcc89a; }
.bg-wood-400 { background-color: #c4a674; }
.bg-wood-500 { background-color: #a68b5b; }
.bg-wood-600 { background-color: #8b7355; }
.bg-wood-700 { background-color: #6d5a47; }
.bg-wood-800 { background-color: #4a3f35; }
.bg-wood-900 { background-color: #2d261f; }

.border-wood-200 { border-color: #e8dcc0; }
.border-wood-300 { border-color: #dcc89a; }
.border-wood-500 { border-color: #a68b5b; }

/* Markdown 容器本身的樣式（scoped 有效，因為直接套在元素上） */
.markdown-content {
  line-height: 1.6;
}

/* 大地色彩系統 */
.text-earth-50 { color: #f7f6f4; }
.text-earth-100 { color: #efede8; }
.text-earth-200 { color: #ddd9d0; }
.text-earth-300 { color: #c7c0b3; }
.text-earth-400 { color: #ada296; }
.text-earth-500 { color: #9a8a7a; }
.text-earth-600 { color: #857662; }
.text-earth-700 { color: #6f6252; }
.text-earth-800 { color: #5a5144; }
.text-earth-900 { color: #47423a; }

.bg-earth-50 { background-color: #f7f6f4; }
.bg-earth-100 { background-color: #efede8; }
.bg-earth-200 { background-color: #ddd9d0; }
.bg-earth-300 { background-color: #c7c0b3; }
.bg-earth-400 { background-color: #ada296; }
.bg-earth-500 { background-color: #9a8a7a; }
.bg-earth-600 { background-color: #857662; }
.bg-earth-700 { background-color: #6f6252; }
.bg-earth-800 { background-color: #5a5144; }
.bg-earth-900 { background-color: #47423a; }

/* SEO-only H1 - 對搜尋引擎可見，但視覺上隱藏 */
.seo-only-h1 {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* 確保對螢幕閱讀器友好 */
.seo-only-h1:focus {
  position: static;
  width: auto;
  height: auto;
  padding: inherit;
  margin: inherit;
  overflow: visible;
  clip: auto;
  white-space: normal;
}

</style>

<!-- v-html 渲染的內容不帶 scoped 屬性，後代選擇器必須用全域樣式才能生效 -->
<style>
/* 標題樣式 */
.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  color: #4a3f35;
  font-weight: 600;
  margin: 0.8em 0 0.4em 0;
  line-height: 1.3;
}

.markdown-content h1 { font-size: 1.5em; }
.markdown-content h2 { font-size: 1.3em; }
.markdown-content h3 { font-size: 1.2em; }
.markdown-content h4 { font-size: 1.1em; }
.markdown-content h5 { font-size: 1em; }
.markdown-content h6 { font-size: 0.9em; }

/* 段落 */
.markdown-content p {
  margin: 0.5em 0;
}

/* 列表 */
.markdown-content li {
  color: #6d5a47;
}

/* 引用 blockquote：左側垂直線 + 縮排 + 淡色 */
.markdown-content blockquote {
  border-left: 3px solid #c4a674;
  margin: 0.8em 0;
  padding: 0.3em 1em;
  color: #8b7355;
  font-style: normal;
  background: transparent;
}

.markdown-content blockquote p {
  margin: 0.2em 0;
  color: inherit;
}

/* 行內程式碼 */
.markdown-content code {
  background-color: #f4f0e6;
  color: #8b7355;
  padding: 0.2em 0.4em;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
}

/* 程式碼區塊 */
.markdown-content pre {
  background-color: #2d261f;
  color: #faf8f3;
  padding: 1em;
  border-radius: 8px;
  overflow-x: auto;
  margin: 1em 0;
  line-height: 1.4;
}

.markdown-content pre code {
  background: none;
  color: inherit;
  padding: 0;
  font-size: 0.9em;
}

/* 連結 */
.markdown-content a {
  color: #a68b5b;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.markdown-content a:hover {
  color: #8b7355;
}

/* 分隔線 */
.markdown-content hr {
  border: none;
  border-top: 2px solid #e8dcc0;
  margin: 1.5em 0;
}

/* 表格 */
.markdown-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
}

.markdown-content th,
.markdown-content td {
  border: 1px solid #e8dcc0;
  padding: 0.5em;
  text-align: left;
}

.markdown-content th {
  background-color: #f4f0e6;
  color: #4a3f35;
  font-weight: 600;
}

/* 強調 */
.markdown-content strong {
  color: #4a3f35;
  font-weight: 600;
}

.markdown-content em {
  color: #6d5a47;
  font-style: italic;
}

/* highlight.js 程式碼高亮 */
.markdown-content .hljs {
  background: #2d261f !important;
  color: #faf8f3 !important;
}

.markdown-content .hljs-comment,
.markdown-content .hljs-quote {
  color: #a68b5b;
  font-style: italic;
}

.markdown-content .hljs-keyword,
.markdown-content .hljs-selector-tag,
.markdown-content .hljs-subst {
  color: #c4a674;
  font-weight: bold;
}

.markdown-content .hljs-number,
.markdown-content .hljs-literal,
.markdown-content .hljs-variable,
.markdown-content .hljs-template-variable {
  color: #dcc89a;
}

.markdown-content .hljs-string,
.markdown-content .hljs-doctag {
  color: #e8dcc0;
}

.markdown-content .hljs-title,
.markdown-content .hljs-section,
.markdown-content .hljs-selector-id {
  color: #f4f0e6;
  font-weight: bold;
}

.markdown-content .hljs-type,
.markdown-content .hljs-class .hljs-title {
  color: #c4a674;
}

.markdown-content .hljs-tag,
.markdown-content .hljs-name,
.markdown-content .hljs-attribute {
  color: #a68b5b;
}

.markdown-content .hljs-attr {
  color: #dcc89a;
}

.markdown-content .hljs-symbol,
.markdown-content .hljs-bullet,
.markdown-content .hljs-link {
  color: #8b7355;
}
</style>